import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE = "SendVertex";
const DEFAULT_DESC = "SendVertex is a deliverability-first email marketing platform: verified lists, smart audiences, campaigns, automations and real-time analytics.";

const PUBLIC = {
  "/": { title: "SendVertex — Deliverability-first Email Marketing Platform", desc: DEFAULT_DESC },
  "/login": { title: "Log in", desc: "Log in to your SendVertex account to manage lists, campaigns and deliverability." },
  "/register": { title: "Create your free account", desc: "Start sending with SendVertex — verified lists, smart audiences and inbox-ready campaigns." },
  "/forgot-password": { title: "Reset your password", desc: "Forgot your SendVertex password? Get a secure reset link by email." },
  "/reset-password": { title: "Choose a new password", desc: "Set a new password for your SendVertex account." },
};

const APP = [
  ["/app/contacts/import", "Create list from CSV", "Upload a CSV, map columns and create a new contact list."],
  ["/app/contacts/", "Contact profile", "Full profile, activity and fields for a contact."],
  ["/app/contacts", "Contacts", "Every record in your Email Database, with filters, verification and lists."],
  ["/app/campaigns/", "Campaign", "Build, schedule and analyse an email campaign."],
  ["/app/campaigns", "Campaigns", "Create, schedule, pause and track your email campaigns."],
  ["/app/automations/", "Automation", "Edit an automated email workflow."],
  ["/app/automations", "Automations", "Automated email workflows triggered by contact behaviour."],
  ["/app/templates", "Templates", "Reusable email templates with a drag-and-drop editor."],
  ["/app/lists", "Lists", "Static contact lists, splits, verification health and quarantine log."],
  ["/app/segments", "Segments", "Dynamic segments built from fields and engagement behaviour."],
  ["/app/audiences", "Smart Audiences", "AI-assisted audiences built from your Email Database."],
  ["/app/email-database", "Email Database", "Your complete contact database with custom fields and verification."],
  ["/app/verification", "Email Verification", "Verify single emails or bulk lists before sending."],
  ["/app/ai-tools", "AI Tools", "AI writing tools for subject lines and email content."],
  ["/app/suppressions", "Suppressions", "Emails and domains that will never be sent to."],
  ["/app/bounces", "Bounces", "Bounce log and classification."],
  ["/app/deliverability", "Deliverability", "Inbox placement, reputation and warmup insights."],
  ["/app/smtp", "SMTP Servers", "Connect and manage sending servers."],
  ["/app/sender-profiles", "Sender Profiles", "From names, addresses and reply-to settings."],
  ["/app/domains", "Domains", "Sending domains and authentication records."],
  ["/app/dns-inspector", "DNS Inspector", "Check SPF, DKIM, DMARC and MX records."],
  ["/app/ip-monitor", "IP Monitor", "Blocklist and reputation monitoring for sending IPs."],
  ["/app/postmaster", "Postmaster", "Google Postmaster reputation data."],
  ["/app/whitelabel", "White Label", "Brand SendVertex for your clients."],
  ["/app/analytics", "Analytics", "Opens, clicks, real vs bot traffic and revenue analytics."],
  ["/app/settings", "Settings", "Workspace settings, sending limits and frequency caps."],
  ["/app/team", "Team", "Invite teammates and manage roles."],
  ["/app/activity", "Activity", "Audit trail of workspace activity."],
  ["/app/profile", "Profile", "Your personal account settings."],
  ["/app/admin", "Admin", "Platform administration."],
  ["/app", "Dashboard", "Overview of your sending, audience and deliverability."],
];

function setMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", "canonical"); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

function resolve(path) {
  const clean = path.length > 1 ? path.replace(/\/+$/, "") : path;
  if (PUBLIC[clean]) return { ...PUBLIC[clean], path: clean };
  const hit = APP.find(([p]) => (p.endsWith("/") ? clean.startsWith(p) : clean === p || clean.startsWith(`${p}/`)));
  if (hit) return { title: hit[1], desc: hit[2], path: clean };
  return { title: SITE, desc: DEFAULT_DESC, path: clean };
}

export function RouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const m = resolve(pathname);
    const title = m.title.startsWith(SITE) ? m.title : `${m.title} | ${SITE}`;
    const url = `${window.location.origin}${m.path}`;
    document.title = title;
    setMeta("name", "description", m.desc);
    setMeta("property", "og:title", title);
    setMeta("property", "og:description", m.desc);
    setMeta("property", "og:url", url);
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", SITE);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", title);
    setMeta("name", "twitter:description", m.desc);
    setCanonical(url);
  }, [pathname]);
  return null;
}
